import { SectionHeading, CallButton } from "./Buttons";
import * as Icons from "lucide-react";

// Educational section: helps customers choose between INCELL and OLED iPhone screens.

export default function ScreenQuality() {
  return (
    <section id="screen-quality" data-testid="screen-quality-section" className="relative bg-white py-20 md:py-28 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          eyebrow="iPhone Screens 101"
          title={<>INCELL vs OLED.<br /><span className="text-brand-red">Which screen should you choose?</span></>}
          sub="When you crack your iPhone screen, there are two grades of replacement available. Here's the simple, honest breakdown so you can pick what's right for your wallet — and your eyes."
          testid="screen-quality-heading"
          align="center"
        />

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {/* INCELL card */}
          <div data-testid="incell-card" className="relative bg-white border border-slate-200 rounded-3xl p-7 md:p-9 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 border border-slate-200">
                <Icons.DollarSign className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs font-bold uppercase tracking-widest text-slate-600">Budget</span>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">$$</span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-black text-brand-navy tracking-tight">INCELL</h3>
            <p className="text-sm text-slate-500 mt-1 font-medium">LCD-based replacement screen</p>

            {/* Phone-screen preview — INCELL profile */}
            <ScreenPreview variant="incell" />

            <p className="mt-5 text-sm md:text-base text-slate-700 leading-relaxed">
              INCELL is an LCD screen that uses a backlight to shine through the display. It looks fine for everyday use — texting, calls, browsing — but it can't truly match the original Apple screen.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-2.5">
              <Pro icon="ThumbsUp" text="Lower price — saves you money" />
              <Pro icon="ThumbsUp" text="Touch and basic functions work normally" />
              <Pro icon="ThumbsUp" text="Good if you just need it working again" />
              <Con text="Colors look slightly washed out vs. original" />
              <Con text="Blacks appear gray (not deep black)" />
              <Con text="Not as bright in direct sunlight" />
              <Con text="True Tone &amp; auto-brightness may be limited" />
            </div>

            <div className="mt-6 pt-5 border-t border-slate-100">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Best for</span>
              <p className="text-sm text-slate-700 mt-1.5">Older iPhones, secondary devices, or when you need to keep cost down.</p>
            </div>
          </div>

          {/* OLED card — RECOMMENDED */}
          <div data-testid="oled-card" className="relative bg-brand-navy text-white border border-brand-navy rounded-3xl p-7 md:p-9 shadow-2xl glow-red overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-red/30 blur-[100px] pointer-events-none" />
            <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-red text-white text-[10px] font-bold uppercase tracking-widest shadow-md">
              <Icons.Star className="w-3 h-3 fill-white" />
              We recommend
            </div>

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 mb-4">
                <Icons.Sparkles className="w-3.5 h-3.5 text-orange-300" />
                <span className="text-xs font-bold uppercase tracking-widest text-orange-200">Premium</span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight">OLED</h3>
              <p className="text-sm text-white/70 mt-1 font-medium">Apple-grade replacement screen</p>

              {/* Phone-screen preview — OLED profile */}
              <ScreenPreview variant="oled" />

              <p className="mt-5 text-sm md:text-base text-white/85 leading-relaxed">
                OLED is the same display technology Apple uses on iPhone X and newer Pros. Each pixel lights up on its own — that's why blacks are truly black, colors pop, and everything looks crisp.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-2.5">
                <ProDark icon="ThumbsUp" text="Looks and feels like the original Apple screen" />
                <ProDark icon="ThumbsUp" text="True deep blacks &amp; vivid, accurate colors" />
                <ProDark icon="ThumbsUp" text="Brighter — readable in direct sunlight" />
                <ProDark icon="ThumbsUp" text="True Tone &amp; auto-brightness work properly" />
                <ProDark icon="ThumbsUp" text="Better for video, gaming &amp; long use" />
                <ProDark icon="ThumbsUp" text="Higher resale value for your phone" />
              </div>

              <div className="mt-6 pt-5 border-t border-white/15">
                <span className="text-[11px] font-bold uppercase tracking-widest text-orange-300">Best for</span>
                <p className="text-sm text-white/85 mt-1.5">Anyone who wants their iPhone to feel brand new — especially iPhone X and newer.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom recommendation strip */}
        <div className="mt-10 bg-gradient-to-r from-green-50 via-white to-orange-50 border border-slate-200 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="flex items-start md:items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-brand-red text-white grid place-items-center shrink-0 glow-red">
              <Icons.Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-display text-xl md:text-2xl font-black text-brand-navy">Our honest recommendation: choose OLED.</h4>
              <p className="text-sm md:text-base text-slate-600 mt-1">It's worth the extra dollars. You'll thank yourself every time you unlock your phone.</p>
            </div>
          </div>
          <CallButton testid="screen-quality-call-btn">Ask Us Which to Pick</CallButton>
        </div>
      </div>
    </section>
  );
}

function Pro({ icon, text }) {
  const Icon = Icons[icon] || Icons.Check;
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-brand-red mt-0.5 shrink-0" />
      <span className="text-sm text-slate-700">{text}</span>
    </div>
  );
}

function Con({ text }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icons.Minus className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <span className="text-sm text-slate-500">{text}</span>
    </div>
  );
}

function ProDark({ icon, text }) {
  const Icon = Icons[icon] || Icons.Check;
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-orange-300 mt-0.5 shrink-0" />
      <span className="text-sm text-white/90">{text}</span>
    </div>
  );
}

/**
 * ScreenPreview — honest visual demo of the difference between INCELL and OLED.
 * Same vivid wallpaper image rendered through two display profiles via CSS filters.
 *  - INCELL: muted saturation, raised black-point, slightly dimmer + blue-shift
 *  - OLED  : pure black, full saturation, full brightness
 * No external "fake" comparison shots — just the same source image, two profiles.
 */
function ScreenPreview({ variant = "oled" }) {
  const isIncell = variant === "incell";
  // Vivid Miami neon image — best content for demonstrating black-level difference
  const wallpaper =
    "https://images.pexels.com/photos/11457821/pexels-photo-11457821.jpeg?auto=compress&cs=tinysrgb&w=600&h=900&fit=crop";

  // CSS filter pipeline that mimics the visual deficits of INCELL panels
  const incellStyle = {
    filter:
      "saturate(0.62) brightness(0.82) contrast(0.78) hue-rotate(-4deg) opacity(0.95)",
    background: "#222428", // raised black-point — the giveaway of LCD/INCELL
  };
  const oledStyle = {
    filter: "saturate(1.12) contrast(1.08) brightness(1.04)",
    background: "#000000", // true black
  };

  return (
    <div className="mt-6 mb-2 flex items-center gap-4" data-testid={`screen-preview-${variant}`}>
      {/* iPhone-style mockup */}
      <div
        className={`relative w-[110px] h-[200px] md:w-[120px] md:h-[220px] rounded-[26px] p-[6px] shrink-0 ${
          isIncell
            ? "bg-gradient-to-b from-slate-300 to-slate-400 shadow-md"
            : "bg-gradient-to-b from-slate-700 to-black shadow-2xl"
        }`}
      >
        {/* Screen */}
        <div
          className="relative w-full h-full rounded-[20px] overflow-hidden"
          style={isIncell ? incellStyle : oledStyle}
        >
          <img
            src={wallpaper}
            alt={isIncell ? "INCELL screen rendering example" : "OLED screen rendering example"}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          {/* Status bar — content stays the same, but rendering differs */}
          <div className="absolute top-1.5 left-2 right-2 flex items-center justify-between text-[9px] font-bold text-white/95">
            <span className="font-mono">9:41</span>
            <div className="flex items-center gap-0.5">
              <Icons.Signal className="w-2.5 h-2.5" />
              <Icons.Wifi className="w-2.5 h-2.5" />
              <Icons.BatteryFull className="w-3 h-3" />
            </div>
          </div>
          {/* Notch */}
          <div className={`absolute top-1 left-1/2 -translate-x-1/2 w-12 h-3.5 rounded-full ${isIncell ? "bg-slate-900" : "bg-black"}`} />
          {/* Subtle banding overlay for INCELL only — simulates LCD light bleed */}
          {isIncell && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 35%, rgba(0,0,0,0.06) 100%)",
              }}
            />
          )}
        </div>
      </div>

      {/* Caption */}
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] font-bold uppercase tracking-[0.18em] mb-1.5 ${
          isIncell ? "text-slate-500" : "text-orange-300"
        }`}>
          What you see
        </div>
        <div className={`font-display font-extrabold text-base md:text-lg leading-tight ${
          isIncell ? "text-brand-navy" : "text-white"
        }`}>
          {isIncell ? "Slightly muted." : "Vivid + deep blacks."}
        </div>
        <p className={`text-xs leading-relaxed mt-1.5 ${isIncell ? "text-slate-500" : "text-white/70"}`}>
          {isIncell
            ? "Same image — but blacks lift toward gray and colors pull back. Fine for everyday use."
            : "Same image — full saturation, true black, the way Apple intended your iPhone to look."}
        </p>
      </div>
    </div>
  );
}
